/**
 * Mark Lost Modal
 *
 * Modal for marking an opportunity as lost
 */

import { useMemo, useState } from 'react';
import { XCircle, Building2, DollarSign, Check, ChevronsUpDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { cn, formatCurrency } from '@/lib/utils';
import { useCompetitorCompanies } from '../../hooks/useCrmCompanies';
import type { PipelineOpportunity } from '../../types';

interface MarkLostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity: PipelineOpportunity | null;
  onConfirm: (reason?: string) => Promise<void>;
  isLoading?: boolean;
}

const LOSS_REASONS = [
  { value: 'price', label: 'Price too high' },
  { value: 'competitor', label: 'Lost to competitor' },
  { value: 'timing', label: 'Bad timing / Not ready' },
  { value: 'budget', label: 'No budget' },
  { value: 'features', label: 'Missing features' },
  { value: 'no_response', label: 'No response / Ghosted' },
  { value: 'internal', label: 'Internal decision' },
  { value: 'other', label: 'Other' },
];

export function MarkLostModal({
  open,
  onOpenChange,
  opportunity,
  onConfirm,
  isLoading,
}: MarkLostModalProps) {
  const [lossReason, setLossReason] = useState('');
  const [notes, setNotes] = useState('');
  const [competitorId, setCompetitorId] = useState<string>('');
  const [competitorOpen, setCompetitorOpen] = useState(false);

  const { data: competitors = [], isLoading: competitorsLoading } = useCompetitorCompanies();

  const selectedCompetitor = useMemo(
    () => competitors.find((c) => c.id === competitorId),
    [competitors, competitorId],
  );

  const isCompetitorReason = lossReason === 'competitor';
  const competitorRequired = isCompetitorReason && !selectedCompetitor;

  const handleConfirm = async () => {
    if (competitorRequired) return;
    const baseLabel = LOSS_REASONS.find((r) => r.value === lossReason)?.label || lossReason;
    let reasonText: string | undefined;
    if (isCompetitorReason && selectedCompetitor) {
      reasonText = `Lost to competitor: ${selectedCompetitor.name}${notes ? ` — ${notes}` : ''}`;
    } else if (lossReason) {
      reasonText = `${baseLabel}${notes ? `: ${notes}` : ''}`;
    } else {
      reasonText = notes || undefined;
    }
    await onConfirm(reasonText);
  };

  const resetState = () => {
    setLossReason('');
    setNotes('');
    setCompetitorId('');
    setCompetitorOpen(false);
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  if (!opportunity) return null;

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) resetState();
      onOpenChange(newOpen);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            Mark as Lost
          </DialogTitle>
          <DialogDescription>
            Mark this opportunity as lost. This action will move it out of the active pipeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Opportunity Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="font-medium text-gray-900">{opportunity.title}</div>

            {opportunity.account_name && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span>{opportunity.account_name}</span>
              </div>
            )}

            {opportunity.estimated_value != null && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span>{formatCurrency(opportunity.estimated_value, opportunity.currency || 'CAD')}</span>
              </div>
            )}
          </div>

          {/* Loss Reason */}
          <div className="space-y-2">
            <Label htmlFor="lossReason">Reason for Loss</Label>
            <Select value={lossReason} onValueChange={(v) => { setLossReason(v); if (v !== 'competitor') setCompetitorId(''); }} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {LOSS_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Competitor selector (only when reason=competitor) */}
          {isCompetitorReason && (
            <div className="space-y-2">
              <Label>
                Which competitor? <span className="text-red-600">*</span>
              </Label>
              <Popover open={competitorOpen} onOpenChange={setCompetitorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    className={cn(
                      'w-full justify-between font-normal',
                      !selectedCompetitor && 'text-muted-foreground',
                    )}
                    disabled={isLoading || competitorsLoading}
                  >
                    {selectedCompetitor
                      ? selectedCompetitor.name
                      : competitorsLoading
                        ? 'Loading competitors...'
                        : competitors.length === 0
                          ? 'No competitors in CRM'
                          : 'Select competitor...'}
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search competitors..." className="h-9" />
                    <CommandList>
                      <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
                        No competitors match.
                      </CommandEmpty>
                      <CommandGroup>
                        {competitors.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={c.name}
                            onSelect={() => {
                              setCompetitorId(c.id);
                              setCompetitorOpen(false);
                            }}
                          >
                            <Check className={cn('mr-2 h-4 w-4', competitorId === c.id ? 'opacity-100' : 'opacity-0')} />
                            <span className="flex-1 truncate">{c.name}</span>
                            {c.industry && <span className="text-[10px] text-muted-foreground ml-2">{c.industry}</span>}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {competitorRequired && (
                <p className="text-xs text-red-600">Please select which competitor won this deal.</p>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context about why this opportunity was lost..."
              rows={3}
              disabled={isLoading}
            />
          </div>

          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              Lost opportunities are archived and removed from the active pipeline. You can view
              them in reporting.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isLoading || competitorRequired}>
            {isLoading ? 'Marking...' : 'Mark as Lost'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
